type TFormSelector = {
  form: string;
  password: string;
  passwordRepeat: string;
  validationRoot: string;
  submitButton: string;
  checkbox: string;
};

enum ERROR_MESSAGES {
  hasError = "data-has-error",
  password_mismatch = "data-error-password-mismatch",
  minLength = "data-error-min-length",
  noDangerousChars = "data-error-no-dangerous-chars",
  unchecked = "data-error-unchecked",
}

type TErrorMessagesValues =
  (typeof ERROR_MESSAGES)[keyof typeof ERROR_MESSAGES];

const ctaRegistrationValidationRules = {
  minLength: 8,
  hasLetter: /[a-zA-Z]/,
  hasNumber: /[0-9]/,
  noDangerousChars: /[<>;'"/\\]/,
};

const ctaRegistrationSelectors: TFormSelector = {
  form: "#cta-user-registration",
  password: "#cta-reg-password",
  passwordRepeat: "#cta-reg-repeat_password",
  validationRoot: "#cta-user-registration",
  submitButton: "button[type='submit']",
  checkbox: "input[type='checkbox']",
};

function initCtaRegistrationForm() {
  const form = document.querySelector(
    ctaRegistrationSelectors.form,
  ) as HTMLFormElement | null;
  if (!form) {
    return;
  }
  initCtaRegistrationFormValidation(form);
}

initCtaRegistrationForm();

function initCtaRegistrationFormValidation(form: HTMLFormElement) {
  if (!form) {
    console.error("Form not found");
    return;
  }

  const toTest = [
    ctaRegistrationSelectors.password,
    ctaRegistrationSelectors.passwordRepeat,
    ctaRegistrationSelectors.checkbox,
  ];
  const formElements = toTest.map((selector) => ({
    selector: selector as string,
    element: form.querySelector(selector) as HTMLInputElement,
  }));

  if (!formElements.every((element) => element.element)) {
    console.error("Form elements not found");
    return;
  }

  form.addEventListener("input", (event) => onFormChanged(event, formElements));
}

function onFormChanged(
  event: Event,
  formElements: { selector: string; element: HTMLInputElement }[],
) {
  const { target } = event;

  if (!target) {
    console.error("Target not found");
    return;
  }

  const { form } = target as HTMLInputElement;

  if (!form) {
    console.error("Form not found");
    return;
  }

  let errors = [] as TErrorMessagesValues[];
  for (const { selector, element } of formElements) {
    if (selector === ctaRegistrationSelectors.password) {
      errors = [...errors, ...validatePassword(element.value)];
    }

    if (selector === ctaRegistrationSelectors.passwordRepeat) {
      const password = formElements.find(
        (element) => element.selector === ctaRegistrationSelectors.password,
      )?.element;
      const passwordRepeat = formElements.find(
        (element) =>
          element.selector === ctaRegistrationSelectors.passwordRepeat,
      )?.element;
      if (password && passwordRepeat) {
        errors = [
          ...errors,
          ...validateConfirmPassword(password, passwordRepeat),
        ];
      }
    }

    if (selector === ctaRegistrationSelectors.checkbox) {
      errors = [...errors, ...validateCheckBoxes(element)];
    }
  }

  errors = removeDuplicatesFromArray(errors);
  console.log("ERRORS", errors);

  if (errors.length > 0) {
    form
      .querySelector(ctaRegistrationSelectors.submitButton)
      ?.setAttribute("disabled", "");
    updateFormErrors(errors, form);
  } else if (form.checkValidity()) {
    form
      .querySelector(ctaRegistrationSelectors.submitButton)
      ?.removeAttribute("disabled");
    updateFormErrors(errors, form);
  } else {
    form
      .querySelector(ctaRegistrationSelectors.submitButton)
      ?.setAttribute("disabled", "");
    updateFormErrors(errors, form);
  }
}

function removeDuplicatesFromArray<T>(array: T[]): T[] {
  return [...new Set(array)];
}

function validatePassword(value: string) {
  const errors = [] as TErrorMessagesValues[];
  if (value.length < ctaRegistrationValidationRules.minLength) {
    errors.push(ERROR_MESSAGES.minLength);
  }

  if (ctaRegistrationValidationRules.noDangerousChars.test(value)) {
    errors.push(ERROR_MESSAGES.noDangerousChars);
  }

  if (errors.length > 0) errors.push(ERROR_MESSAGES.hasError);
  return errors;
}

function validateConfirmPassword(
  password: HTMLInputElement,
  passwordRepeat: HTMLInputElement,
) {
  const errors = [] as TErrorMessagesValues[];

  if (password.value !== passwordRepeat.value) {
    errors.push(ERROR_MESSAGES.password_mismatch);
  }

  if (errors.length > 0) errors.push(ERROR_MESSAGES.hasError);
  return errors;
}

function validateCheckBoxes(checkbox: HTMLInputElement) {
  const errors = [] as TErrorMessagesValues[];
  if (!checkbox.checked) {
    errors.push(ERROR_MESSAGES.unchecked);
  }
  return errors;
}

function updateFormErrors(errors: ERROR_MESSAGES[], form: HTMLFormElement) {
  for (const error of Object.values(ERROR_MESSAGES)) {
    const formHasError = form.hasAttribute(error);
    const errorInArray = errors.includes(error);

    if (errorInArray && !formHasError) {
      form.setAttribute(error, "");
    }

    if (!errorInArray && formHasError) {
      form.removeAttribute(error);
    }
  }
}
